import { useMemo } from 'react';
import { useCfgParam } from './useCfgParam';
import { getIsMobile } from '../utils/helpers';

const useRoutes = () => {
  const subUrl = useCfgParam('subUrl');
  const isMobile = getIsMobile();

  let urlPrefix: string = isMobile ? '/' : subUrl ? subUrl : '/';

  if (!urlPrefix.startsWith('/')) {
    urlPrefix = `/${urlPrefix}`;
  }
  if (!urlPrefix.endsWith('/')) {
    urlPrefix = `${urlPrefix}/`;
  }

  // Memoized so the returned object keeps a stable identity across renders (only urlPrefix
  // can change it). Consumers use it as a useMemo/useEffect dep — an unstable object would
  // make those deps churn every render. See useActiveMenu.
  return useMemo(
    () => ({
      relative: {
        // Dataset routes
        datasetList: () => `${urlPrefix}dataset/:datasetId`,
        application: () => `${urlPrefix}application/:appId`,
        // Fullscreen export route shares the dataset provider with list/detail pages.
        datasetExport: () =>
          `${urlPrefix}dataset/:datasetId/export/:exportMode`,
        // Fullscreen print route shares the dataset provider the same way, so a
        // report reads the definition, filters and sorting of the live grid.
        datasetPrint: () => `${urlPrefix}dataset/:datasetId/print/:templateId`,
        datasetDetail: () => `${urlPrefix}dataset/:datasetId/detail/:itemId`,
        datasetDetailSubset: () =>
          `${urlPrefix}dataset/:datasetId/detail/:itemId/subset/:subsetId`,

        // Layout editor routes
        layoutEditor: () =>
          `${urlPrefix}dataset/:datasetId/layout/:layoutId/edit/:objectId?`,
        layoutEditorReference: () =>
          `${urlPrefix}dataset/:datasetId/layout/:layoutId/edit/:objectId/reference/:referenceName`,

        // External app route (control action type 'app')
        datasetApp: () => `${urlPrefix}dataset/:datasetId/app`,

        // Filter routes
        datasetDetailMasterFilter: () =>
          `${urlPrefix}dataset/:datasetId/filter`,
        subsetDetailSlaveFilter: () =>
          `${urlPrefix}dataset/:datasetId/item/:itemId/subset/:subsetId/filter`,

        // Reference routes
        masterFilterReference: () =>
          `${urlPrefix}dataset/:datasetId/filter/reference`,
        slaveFilterReference: () =>
          `${urlPrefix}dataset/:datasetId/item/:itemId/subset/:subsetId/filter/reference`,
        datasetListReference: () => `${urlPrefix}dataset/:datasetId/reference`,
        formListAddActionReference: () =>
          `${urlPrefix}dataset/:datasetId/form/:formId/add/reference/:referenceDatasetId/:field`,
        formListCopyActionReference: () =>
          `${urlPrefix}dataset/:datasetId/form/:formId/copy/:sourceItemId/reference/:referenceDatasetId/:field`,
        formListEditActionReference: () =>
          `${urlPrefix}dataset/:datasetId/form/:formId/edit/:itemId/reference/:referenceDatasetId/:field`,
        formSubsetAddActionReference: () =>
          `${urlPrefix}dataset/:datasetId/item/:itemId/subset/:subsetId/form/:formId/add/reference/:referenceDatasetId/:field`,
        formSubsetCopyActionReference: () =>
          `${urlPrefix}dataset/:datasetId/item/:itemId/subset/:subsetId/form/:formId/copy/:subsetItemId/reference/:referenceDatasetId/:field`,
        formSubsetEditActionReference: () =>
          `${urlPrefix}dataset/:datasetId/item/:itemId/subset/:subsetId/form/:formId/edit/:subsetItemId/reference/:referenceDatasetId/:field`,

        // Form routes
        formAddAction: () => `${urlPrefix}form/:formId/add`,
        formAddActionReference: () =>
          `${urlPrefix}form/:formId/add/reference/:referenceDatasetId/:field`,
        formListAddAction: () =>
          `${urlPrefix}dataset/:datasetId/form/:formId/add`,
        formListCopyAction: () =>
          `${urlPrefix}dataset/:datasetId/form/:formId/copy/:sourceItemId`,
        formListEditAction: () =>
          `${urlPrefix}dataset/:datasetId/form/:formId/edit/:itemId`,
        formSubsetAddAction: () =>
          `${urlPrefix}dataset/:datasetId/item/:itemId/subset/:subsetId/form/:formId/add`,
        formSubsetCopyAction: () =>
          `${urlPrefix}dataset/:datasetId/item/:itemId/subset/:subsetId/form/:formId/copy/:subsetItemId`,
        formSubsetEditAction: () =>
          `${urlPrefix}dataset/:datasetId/item/:itemId/subset/:subsetId/form/:formId/edit/:subsetItemId`,

        // Fallback routes
        notImplemented: () => `${urlPrefix}not-implemented`,
      },
      home: () => urlPrefix,
      dashboards: () => `${urlPrefix}dashboards`,
      guides: () => `${urlPrefix}guides`,
      setEndpoint: () => `${urlPrefix}setEndpoint`,

      // Dataset routes
      datasetList: (datasetId: string) => `${urlPrefix}dataset/${datasetId}`,
      application: (appId: string) =>
        `${urlPrefix}application/${encodeURIComponent(appId)}`,
      // Fullscreen export route shares the dataset provider with list/detail pages.
      datasetExport: (datasetId: string, exportMode = 'main') =>
        `${urlPrefix}dataset/${datasetId}/export/${exportMode}`,
      datasetPrint: (datasetId: string, templateId: string) =>
        `${urlPrefix}dataset/${datasetId}/print/${templateId}`,
      subset: (datasetId: string, itemId: string, subsetId: string) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}`,
      subsetDetail: (datasetId: string, itemId: string) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}`,
      datasetDetail: (datasetId: string, itemId: string) =>
        `${urlPrefix}dataset/${datasetId}/detail/${itemId}`,
      datasetDetailSubset: (
        datasetId: string,
        itemId: string,
        subsetId: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/detail/${itemId}/subset/${subsetId}`,

      // Layout editor routes
      layoutEditor: (datasetId: string, layoutId: string, objectId?: string) =>
        `${urlPrefix}dataset/${datasetId}/layout/${layoutId}/edit${
          objectId ? `/${objectId}` : ''
        }`,
      layoutEditorReference: (
        datasetId: string,
        layoutId: string,
        objectId: string,
        referenceName: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/layout/${layoutId}/edit/${objectId}/reference/${referenceName}`,

      // External app route (control action type 'app')
      datasetApp: (datasetId: string) => `${urlPrefix}dataset/${datasetId}/app`,

      // Filter routes
      datasetDetailMasterFilter: (datasetId: string) =>
        `${urlPrefix}dataset/${datasetId}/filter`,
      subsetDetailSlaveFilter: (
        datasetId: string,
        itemId: string,
        subsetId: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}/filter`,

      // Reference routes
      masterFilterReference: (datasetId: string) =>
        `${urlPrefix}dataset/${datasetId}/filter/reference`,
      slaveFilterReference: (
        datasetId: string,
        itemId: string,
        subsetId: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}/filter/reference`,
      formListAddActionReference: (
        datasetId: string,
        formId: string,
        referenceDatasetId: string,
        field: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/form/${formId}/add/reference/${referenceDatasetId}/${field}`,
      formListCopyActionReference: (
        datasetId: string,
        formId: string,
        sourceItemId: string,
        referenceDatasetId: string,
        field: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/form/${formId}/copy/${sourceItemId}/reference/${referenceDatasetId}/${field}`,
      formListEditActionReference: (
        datasetId: string,
        formId: string,
        itemId: string,
        referenceDatasetId: string,
        field: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/form/${formId}/edit/${itemId}/reference/${referenceDatasetId}/${field}`,
      formSubsetAddActionReference: (
        datasetId: string,
        itemId: string,
        subsetId: string,
        formId: string,
        referenceDatasetId: string,
        field: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}/form/${formId}/add/reference/${referenceDatasetId}/${field}`,
      formSubsetCopyActionReference: (
        datasetId: string,
        itemId: string,
        subsetId: string,
        formId: string,
        subsetItemId: string,
        referenceDatasetId: string,
        field: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}/form/${formId}/copy/${subsetItemId}/reference/${referenceDatasetId}/${field}`,
      formSubsetEditActionReference: (
        datasetId: string,
        itemId: string,
        subsetId: string,
        formId: string,
        subsetItemId: string,
        referenceDatasetId: string,
        field: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}/form/${formId}/edit/${subsetItemId}/reference/${referenceDatasetId}/${field}`,

      // Form routes
      formAddAction: (formId: string) => `${urlPrefix}form/${formId}/add`,
      formAddActionReference: (
        formId: string,
        referenceDatasetId: string,
        field: string
      ) =>
        `${urlPrefix}form/${formId}/add/reference/${referenceDatasetId}/${field}`,
      formListAddAction: (datasetId: string, formId: string) =>
        `${urlPrefix}dataset/${datasetId}/form/${formId}/add`,
      formListCopyAction: (
        datasetId: string,
        formId: string,
        sourceItemId: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/form/${formId}/copy/${sourceItemId}`,
      formListEditAction: (datasetId: string, formId: string, itemId: string) =>
        `${urlPrefix}dataset/${datasetId}/form/${formId}/edit/${itemId}`,
      formSubsetAddAction: (
        datasetId: string,
        itemId: string,
        subsetId: string,
        formId: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}/form/${formId}/add`,
      formSubsetCopyAction: (
        datasetId: string,
        itemId: string,
        subsetId: string,
        formId: string,
        subsetItemId: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}/form/${formId}/copy/${subsetItemId}`,
      formSubsetEditAction: (
        datasetId: string,
        itemId: string,
        subsetId: string,
        formId: string,
        subsetItemId: string
      ) =>
        `${urlPrefix}dataset/${datasetId}/item/${itemId}/subset/${subsetId}/form/${formId}/edit/${subsetItemId}`,

      // Fallback routes
      notImplemented: () => `${urlPrefix}not-implemented`,
    }),
    [urlPrefix]
  );
};

export default useRoutes;
